import { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/button';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    advertising: false
  });
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check if user has already given valid consent
    const checkConsent = () => {
      try {
        const consent = localStorage.getItem('cookie_consent');
        const prefs = localStorage.getItem('cookie_preferences');
        
        // Valid consent requires BOTH consent status AND preferences
        const hasValidConsent = consent && prefs && 
          (consent === 'accepted' || consent === 'declined' || consent === 'partial');
        
        if (!hasValidConsent) {
          // No valid consent - show banner immediately
          console.log('[Cookie Consent] No valid consent found, showing banner');
          setShowBanner(true);
        } else {
          console.log('[Cookie Consent] Valid consent found:', consent);
          setShowBanner(false);
        }
      } catch (error) {
        console.error('[Cookie Consent] Error checking consent:', error);
        // On error, show banner to be safe
        setShowBanner(true);
      } finally {
        setIsChecking(false);
      }
    };

    // Check immediately on mount
    checkConsent();
  }, []);

  const updateConsent = (prefs) => {
    // Update Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.advertising ? 'granted' : 'denied',
        'ad_user_data': prefs.advertising ? 'granted' : 'denied',
        'ad_personalization': prefs.advertising ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    console.log('[Cookie Consent] User accepted all cookies');
    const allAccepted = {
      necessary: true,
      analytics: true,
      advertising: true
    };
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_preferences', JSON.stringify(allAccepted));
    updateConsent(allAccepted);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectNonEssential = () => {
    console.log('[Cookie Consent] User rejected non-essential cookies');
    const essentialOnly = {
      necessary: true,
      analytics: false,
      advertising: false
    };
    localStorage.setItem('cookie_consent', 'declined');
    localStorage.setItem('cookie_preferences', JSON.stringify(essentialOnly));
    updateConsent(essentialOnly);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    console.log('[Cookie Consent] User saved custom preferences:', preferences);
    const status = preferences.analytics || preferences.advertising ? 'partial' : 'declined';
    localStorage.setItem('cookie_consent', status);
    localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
    updateConsent(preferences);
    setShowBanner(false);
    setShowPreferences(false);
  };

  // Don't render anything while checking (prevents flash)
  if (isChecking) return null;
  
  // Only render banner if needed
  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white border-t-2 md:border-2 border-gray-200 md:rounded-lg shadow-2xl w-full md:max-w-2xl md:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {!showPreferences ? (
            // Main Banner
            <>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Cookie className="h-8 w-8 text-[#2563EB] flex-shrink-0" />
                  <h3 className="text-xl font-bold text-gray-900">Cookie Preferences</h3>
                </div>
              </div>
              
              <p className="text-sm text-gray-700 mb-4">
                We use cookies to improve your experience, understand website usage through analytics, and measure the effectiveness of our advertising campaigns.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Your choices:</strong>
                </p>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li><strong>Necessary:</strong> Always active (required for website functionality)</li>
                  <li><strong>Analytics:</strong> Google Analytics (understand how visitors use our site)</li>
                  <li><strong>Advertising:</strong> Google Ads conversion tracking (measure campaign performance)</li>
                </ul>
              </div>
              
              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleAcceptAll}
                  className="w-full bg-[#2563EB] hover:bg-blue-700 text-white"
                  size="lg"
                >
                  Accept All
                </Button>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setShowPreferences(true)}
                    variant="outline"
                    className="flex-1 gap-2"
                    size="lg"
                  >
                    <Settings className="h-4 w-4" />
                    Manage Preferences
                  </Button>
                  <Button
                    onClick={handleRejectNonEssential}
                    variant="outline"
                    className="flex-1"
                    size="lg"
                  >
                    Reject Non-Essential
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                Learn more in our{' '}
                <a href="/privacy" className="text-[#2563EB] hover:underline">
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a href="/cookies" className="text-[#2563EB] hover:underline">
                  Cookie Policy
                </a>
              </p>
            </>
          ) : (
            // Preferences Panel
            <>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Settings className="h-8 w-8 text-[#2563EB] flex-shrink-0" />
                  <h3 className="text-xl font-bold text-gray-900">Cookie Preferences</h3>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Back"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">Necessary Cookies</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Always Active</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Essential for website functionality. Cannot be disabled.
                  </p>
                </div>

                {/* Analytics */}
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">Analytics Cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2563EB]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Google Analytics helps us understand how visitors use our website.
                  </p>
                </div>

                {/* Advertising */}
                <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">Advertising Cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.advertising}
                        onChange={(e) => setPreferences(prev => ({ ...prev, advertising: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#F97316]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Google Ads conversion tracking to measure advertising campaign performance.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="w-full bg-[#2563EB] hover:bg-blue-700 text-white"
                  size="lg"
                >
                  Save My Preferences
                </Button>
                <Button
                  onClick={() => setShowPreferences(false)}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  Back
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
